/**
 * Widget V2 — Booking / game detail
 *
 * Detail page for one booking, reached via /:slug/bookings/:bookingId. For a
 * split booking (open game) it adds the seat roster (host / paid / pending /
 * open), the shareable claim link, and the host's hold status + capture
 * deadline. Works for plain bookings too — they just get the summary card.
 * Seat names are server-gated to logged-in viewers, mirroring the class roster.
 */

import React from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DateTime } from "luxon";

import { LoadingSpinner, EmptyState } from "/imports/ui/components/shared";
import { PageHeading, SECTION_LABEL_SX, DISCOVERY_CARD_SX } from "/imports/ui/components/discovery";
import useBookingGame from "./useBookingGame";

const SEAT_CHIP = {
  HOST: { label: "Host", color: "primary" },
  PAID: { label: "Paid", color: "success" },
  CLAIMED: { label: "Pending", color: "warning" },
  OPEN: { label: "Open", color: "default" }
};

function formatDue(iso) {
  // captureDueAt is REAL UTC — plain fromISO converts to the viewer's clock.
  const dt = DateTime.fromISO(iso);
  return dt.isValid ? dt.toFormat("ccc, MMM d 'at' h:mm a") : "";
}

function SeatTile({ seat, isViewerAuthenticated }) {
  const chip = SEAT_CHIP[seat.state] || SEAT_CHIP.OPEN;
  const isOpen = seat.state === "OPEN";
  const named = !!seat.displayName;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "4.75rem", flexShrink: 0 }}>
      <Avatar
        alt={seat.displayName || "Open spot"}
        sx={{
          width: "3rem",
          height: "3rem",
          fontWeight: 700,
          fontSize: "1.0625rem",
          ...(isOpen
            ? { bgcolor: "transparent", color: "text.disabled", border: "2px dashed", borderColor: "divider" }
            : { bgcolor: (t) => alpha(t.palette.primary.main, 0.15), color: "primary.dark" }),
          ...(seat.isMe && { border: "2px solid", borderColor: "primary.main" })
        }}
      >
        {isOpen ? <PersonAddAltIcon sx={{ fontSize: "1.25rem" }} /> : seat.initials || "?"}
      </Avatar>
      <Typography variant="caption" noWrap sx={{ mt: 0.75, fontWeight: 600, maxWidth: "100%", ...(seat.isMe && { color: "primary.main" }) }}>
        {isOpen ? "Open" : seat.isMe ? "You" : named ? seat.displayName : isViewerAuthenticated ? "Player" : "Hidden"}
      </Typography>
      <Chip label={chip.label} color={chip.color} size="small" variant="outlined" sx={{ mt: 0.5, height: "1.125rem", fontSize: "0.6rem" }} />
    </Box>
  );
}

// Host-facing (and lightly guest-facing) lifecycle banner for the game.
function GameStatusBanner({ game, currency, goHold }) {
  if (game.status === "PENDING_AUTH") {
    if (game.holdToken) {
      // Red, not amber: the court is not held until the host authorizes, and an abandoned
      // hold releases the booking outright — the same stakes as a failed capture. The button
      // carries the only action that saves the booking, so it is a solid CTA rather than an
      // Alert action, which renders as quiet inherit-coloured text.
      return (
        <Alert
          severity="error"
          sx={{
            borderRadius: 3,
            mb: 2,
            fontSize: { xs: "0.8125rem", md: "0.875rem" },
            alignItems: "center",
            "& .MuiAlert-message": { flex: 1, minWidth: 0 }
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: 1.5
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>Your court isn't secured yet — authorize the card hold to activate this game.</Box>
            <Button
              variant="contained"
              color="error"
              size="small"
              onClick={() => goHold(game.holdToken)}
              sx={{ flexShrink: 0, fontWeight: 700, whiteSpace: "nowrap" }}
            >
              Authorize
            </Button>
          </Box>
        </Alert>
      );
    }
    return (
      <Alert severity="info" sx={{ borderRadius: 3, mb: 2, fontSize: { xs: "0.8125rem", md: "0.875rem" }, alignItems: "center" }}>
        This game isn't active yet — the host hasn't confirmed their card hold.
      </Alert>
    );
  }

  if (game.status === "OPEN" && game.isHost && game.captureDueAt) {
    const exposure = game.openCount + (game.claimedCount || 0) > 0 && game.captureAmount != null ? game.captureAmount : null;
    return (
      <Alert severity="info" sx={{ borderRadius: 3, mb: 2, fontSize: { xs: "0.8125rem", md: "0.875rem" }, alignItems: "center" }}>
        {exposure != null && exposure > 0
          ? `Unpaid spots (${currency} ${exposure.toFixed(2)} right now) will be charged to your card on ${formatDue(game.captureDueAt)}.`
          : `All spots are paid — only your own share will be charged on ${formatDue(game.captureDueAt)}.`}
      </Alert>
    );
  }

  if (game.status === "CAPTURE_FAILED" && game.shortfallChargeLink) {
    return (
      <Alert
        severity="error"
        sx={{ borderRadius: 3, mb: 2, fontSize: { xs: "0.8125rem", md: "0.875rem" }, alignItems: "center" }}
        action={
          <Button color="inherit" size="small" onClick={() => (window.location.href = game.shortfallChargeLink)}>
            Pay now
          </Button>
        }
      >
        We couldn't charge your card for the unpaid spots — please settle the remaining balance.
      </Alert>
    );
  }

  if ((game.status === "CAPTURED" || game.status === "CAPTURING") && game.isHost) {
    return (
      <Alert severity="success" sx={{ borderRadius: 3, mb: 2, fontSize: { xs: "0.8125rem", md: "0.875rem" }, alignItems: "center" }}>
        Your share {game.status === "CAPTURED" ? "has been" : "is being"} charged to your card — this game is all settled.
      </Alert>
    );
  }

  if (game.status === "CANCELED" || game.status === "RELEASED") {
    return (
      <Alert severity="warning" sx={{ borderRadius: 3, mb: 2, fontSize: { xs: "0.8125rem", md: "0.875rem" }, alignItems: "center" }}>
        This game was cancelled.
      </Alert>
    );
  }

  return null;
}

function BookingGameView() {
  const { slug, currency, booking, loading, error, goBack, navigate, isAuthenticated, copyShareLink, goClaim, goHold } = useBookingGame();

  if (loading) {
    return (
      <Box>
        <PageHeading title="Booking" onBack={goBack} />
        <LoadingSpinner />
      </Box>
    );
  }

  if (error || !booking) {
    return (
      <Box>
        <PageHeading title="Booking" onBack={goBack} />
        <EmptyState
          title="Booking not found"
          description="This booking is no longer available."
          actionLabel="Back to home"
          onAction={() => navigate(`/${slug}`)}
        />
      </Box>
    );
  }

  const game = booking.openGame;
  const displayCurrency = (game?.currency || booking.invoice?.currency || currency || "AED").toUpperCase();

  // Booking dates are fake UTC — club wall-clock stored with a +00:00 offset.
  const start = booking.startDate ? DateTime.fromISO(booking.startDate, { zone: "utc" }) : null;
  const end = booking.endDate ? DateTime.fromISO(booking.endDate, { zone: "utc" }) : null;
  const dateLabel = start?.isValid ? start.toFormat("cccc, MMM d") : "";
  const timeLabel = start?.isValid && end?.isValid ? `${start.toFormat("h:mm a")} – ${end.toFormat("h:mm a")}` : "";

  const isParticipant = !!game?.seats?.some((s) => s.isMe);
  const canClaim = !!game && game.status === "OPEN" && game.openCount > 0 && !isParticipant;
  const canShare = !!game && (game.status === "OPEN" || game.status === "PENDING_AUTH") && game.openCount > 0 && isParticipant;

  return (
    <Box>
      <PageHeading title={game ? "Your game" : "Booking"} onBack={goBack} />

      <Box sx={{ ...DISCOVERY_CARD_SX, overflow: "hidden" }}>
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            background: (t) => `linear-gradient(120deg, ${alpha(t.palette.primary.main, 0.2)} 0%, ${alpha(t.palette.primary.main, 0.05)} 100%)`
          }}
        >
          {/* Steps below the PageHeading (1.4rem on xs) so the card title doesn't
              outweigh the page title it sits under on a phone. */}
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: "-0.01em", fontSize: { xs: "1.15rem", md: "1.5rem" } }}>
            {booking.service?.name || "Court booking"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {[dateLabel, timeLabel, booking.duration ? `${booking.duration} min` : null].filter(Boolean).join(" · ")}
          </Typography>
          {(booking.facility || booking.location) && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {[booking.facility?.name, booking.location?.name].filter(Boolean).join(" · ")}
            </Typography>
          )}
          {booking.trainer && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              with {booking.trainer.name}
            </Typography>
          )}
        </Box>

        {game && (
          <Box sx={{ p: { xs: 2, md: 3 }, pt: 2 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
              <Typography variant="body2" color="text.secondary">
                Each player pays
              </Typography>
              {/* Stays under the card title above it — the share is a detail of
                  the booking, not the headline. */}
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}>
                {displayCurrency} {(Number(game.shareAmount) || 0).toFixed(2)}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {game.paidCount + (game.seats?.some((s) => s.state === "HOST") ? 1 : 0)} of {game.seatsTotal} spots covered · {game.openCount} open
            </Typography>
          </Box>
        )}
      </Box>

      {game && (
        <Box sx={{ mt: { xs: 2, md: 3 } }}>
          <GameStatusBanner game={game} currency={displayCurrency} goHold={goHold} />

          <Typography sx={{ ...SECTION_LABEL_SX, mb: 1.5 }}>
            Players · {game.seatsTotal - game.openCount} of {game.seatsTotal}
          </Typography>

          {!isAuthenticated && (
            <Box
              sx={{
                ...DISCOVERY_CARD_SX,
                p: { xs: 2, md: 2.5 },
                mb: 2,
                display: "flex",
                // Icon + copy + button crush each other on a narrow phone, so the
                // button drops to its own line and spans the card there.
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 1.5,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.04)
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                <LockOutlinedIcon sx={{ color: "text.secondary", flexShrink: 0 }} />
                <Typography variant="body2" color="text.secondary">
                  Log in to see who's playing and claim your spot.
                </Typography>
              </Box>
              <Button size="small" variant="outlined" onClick={goClaim} sx={{ flexShrink: 0, width: { xs: "100%", sm: "auto" } }}>
                Claim a spot
              </Button>
            </Box>
          )}

          <Box
            sx={{
              ...DISCOVERY_CARD_SX,
              p: { xs: 1.5, md: 2 },
              display: "flex",
              gap: 1,
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none"
            }}
          >
            {(game.seats || []).map((seat, i) => (
              <SeatTile key={i} seat={seat} isViewerAuthenticated={isAuthenticated} />
            ))}
          </Box>

          <Box sx={{ mt: 2.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
            {canClaim && isAuthenticated && (
              <Button variant="contained" size="large" fullWidth onClick={goClaim}>
                Claim a spot — {displayCurrency} {(Number(game.shareAmount) || 0).toFixed(2)}
              </Button>
            )}
            {canShare && (
              <Button variant={canClaim ? "outlined" : "contained"} size="large" fullWidth startIcon={<ContentCopyIcon />} onClick={copyShareLink}>
                Copy booking link
              </Button>
            )}
          </Box>
        </Box>
      )}

      {!game && booking.invoice && (
        <Box sx={{ ...DISCOVERY_CARD_SX, p: 2.5, mt: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Your payment
          </Typography>
          <Chip
            label={booking.invoice.status === "PAID" || booking.invoice.status === "SETTLED" ? "Paid" : booking.invoice.status}
            color={booking.invoice.status === "PAID" || booking.invoice.status === "SETTLED" ? "success" : "default"}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}

export default BookingGameView;
